import { useState, useEffect, useCallback } from 'react';
import { Modal } from 'react-bootstrap-v5';
import moment from 'moment';
import Swal from 'sweetalert2';
import { IArquivoRetorno } from '../../interfaces/IArquivoRetorno';
import ArquivoRetornoService from '../../services/ArquivoRetornoService';
import type { FiltrosBook } from '../../services/ArquivoRetornoService';
import ModalFiltrosBook from './modal-filtros-book';

interface Props {
    esteiraID: number;
    empresaID: number;
    esteira: any;
    show: boolean;
    toggleModal: () => void;
}

function statusInfo(arquivo: IArquivoRetorno): { label: string; color: string; bg: string } {
    if (arquivo.erro)
        return { label: 'ERRO', color: '#d9534f', bg: '#d9534f22' };
    if (arquivo.processando)
        return { label: 'PROCESSANDO', color: '#f0ad4e', bg: '#f0ad4e22' };
    if (arquivo.processou)
        return { label: 'CONCLUÍDO', color: '#5cb85c', bg: '#5cb85c22' };
    return { label: 'AGUARDANDO', color: '#6c757d', bg: '#6c757d22' };
}

function ModalArquivosRetorno({ esteiraID, empresaID, esteira, show, toggleModal }: Props) {
    const [dados, setDados] = useState<IArquivoRetorno[]>([]);
    const [loading, setLoading] = useState(false);
    const [gerando, setGerando] = useState(false);
    const [baixando, setBaixando] = useState<number | null>(null);
    const [pollingInterval, setPollingInterval] = useState<ReturnType<typeof setInterval> | null>(null);
    const [showFiltros, setShowFiltros] = useState(false);

    const buscar = useCallback(async () => {
        if (!esteiraID) return;
        setLoading(true);
        try {
            const res = await ArquivoRetornoService.listar(esteiraID);
            setDados(res);
        } catch {
            // silencioso
        } finally {
            setLoading(false);
        }
    }, [esteiraID]);

    useEffect(() => {
        if (show && esteiraID) {
            buscar();
        }
    }, [show, esteiraID, buscar]);

    // Polling enquanto houver arquivos processando
    useEffect(() => {
        const temProcessando = dados.some(d => d.processando);

        if (temProcessando && show) {
            if (!pollingInterval) {
                const interval = setInterval(buscar, 8000);
                setPollingInterval(interval);
            }
        } else {
            if (pollingInterval) {
                clearInterval(pollingInterval);
                setPollingInterval(null);
            }
        }

        return () => {
            if (pollingInterval) clearInterval(pollingInterval);
        };
    }, [dados, show]);

    const handleGerar = useCallback(async (filtros: FiltrosBook, contasSelecionadas: number[]) => {
        if (!esteiraID || !empresaID || !esteira) return;

        setShowFiltros(false);
        setGerando(true);
        try {
            await ArquivoRetornoService.gerar(esteiraID, {
                empresaId: empresaID,
                inicio: esteira.periodoInicio,
                fim: esteira.periodoFim,
                mostrarContaReferencial: true,
                ...filtros,
                contasSelecionadas: contasSelecionadas.length > 0 ? contasSelecionadas : undefined
            });

            Swal.fire({
                text: 'Geração do Book iniciada! O arquivo ficará disponível para download quando concluído.',
                icon: 'success',
                timer: 4000,
                showConfirmButton: false
            });

            await buscar();
        } catch {
            Swal.fire({
                text: 'Erro ao iniciar geração do Book. Tente novamente.',
                icon: 'error',
                timer: 3000,
                showConfirmButton: false
            });
        } finally {
            setGerando(false);
        }
    }, [esteiraID, empresaID, esteira, buscar]);

    const handleDownload = useCallback(async (arquivo: IArquivoRetorno) => {
        if (!arquivo.processou || arquivo.erro) return;

        setBaixando(arquivo.id);
        try {
            const url = await ArquivoRetornoService.obterUrlDownload(arquivo.id);
            const link = document.createElement('a');
            link.href = url;
            link.download = arquivo.nomeArquivo;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch {
            Swal.fire({
                text: 'Erro ao gerar link de download. Tente novamente.',
                icon: 'error',
                timer: 3000,
                showConfirmButton: false
            });
        } finally {
            setBaixando(null);
        }
    }, []);

    return (
        <Modal size='lg' centered show={show} onHide={toggleModal}>
            <div className='modal-content' style={{ backgroundColor: '#F4F5F7' }}>
                <div className='modal-header'>
                    <h5 className='modal-title titulo-modal'>Arquivos de Retorno</h5>
                    <div className='btn btn-icon btn-sm btn-active-light-primary ms-2'>
                        <i onClick={toggleModal} className='fas fa-times' style={{ color: '#5E6397', cursor: 'pointer' }} />
                    </div>
                </div>

                <div className='modal-body'>
                    {/* Ação de gerar */}
                    <div className='card card-body mb-4 p-3' style={{ backgroundColor: '#fff', border: '1px solid #e4e6ef' }}>
                        <div className='d-flex align-items-center justify-content-between flex-wrap gap-2'>
                            <div>
                                <div className='fw-bold' style={{ fontSize: 13 }}>Gerar Book</div>
                            </div>
                            <button
                                className='btn btn-sm btn-primary d-flex align-items-center gap-2'
                                onClick={() => setShowFiltros(true)}
                                disabled={gerando}
                            >
                                {gerando ? (
                                    <span className='spinner-border spinner-border-sm' />
                                ) : (
                                    <i className='fas fa-file-pdf' />
                                )}
                                {gerando ? 'Gerando...' : 'Gerar PDF'}
                            </button>
                        </div>
                    </div>

                    <ModalFiltrosBook
                        esteiraID={esteiraID}
                        show={showFiltros}
                        onConfirm={handleGerar}
                        onCancel={() => setShowFiltros(false)}
                    />

                    {/* Contagem */}
                    <div className='d-flex justify-content-between align-items-center mb-3'>
                        <span className='fw-semibold' style={{ fontSize: 13 }}>Arquivos gerados</span>
                        <button
                            className='btn btn-sm btn-light-secondary d-flex align-items-center gap-1'
                            onClick={buscar}
                            disabled={loading}
                            style={{ fontSize: 12 }}
                        >
                            <i className='fas fa-sync-alt' style={{ fontSize: 11 }} />
                            Atualizar
                        </button>
                    </div>

                    {/* Tabela */}
                    {loading && dados.length === 0 ? (
                        <div className='d-flex justify-content-center py-5'>
                            <span className='spinner-border spinner-border-sm text-primary me-2 mt-1' />
                            <span>Carregando...</span>
                        </div>
                    ) : dados.length === 0 ? (
                        <div style={{ textAlign: 'center', padding: '30px', color: '#6c757d' }}>
                            Nenhum arquivo de retorno gerado ainda.
                        </div>
                    ) : (
                        <div style={{ overflowX: 'auto' }}>
                            <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-3 mb-0'>
                                <thead>
                                    <tr className='fw-bold text-muted' style={{ fontSize: 12 }}>
                                        <th>Arquivo</th>
                                        <th>Gerado em</th>
                                        <th className='text-center'>Status</th>
                                        <th className='text-center'>Download</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {dados.map(arquivo => {
                                        const st = statusInfo(arquivo);
                                        return (
                                            <tr key={arquivo.id}>
                                                <td style={{ fontSize: 12, maxWidth: 260 }}>
                                                    <span
                                                        title={arquivo.nomeArquivo}
                                                        style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                                                    >
                                                        {arquivo.nomeArquivo}
                                                    </span>
                                                </td>
                                                <td style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
                                                    {moment(arquivo.dataArquivo).format('DD/MM/YYYY HH:mm')}
                                                </td>
                                                <td className='text-center'>
                                                    {arquivo.processando ? (
                                                        <span className='d-flex align-items-center justify-content-center gap-1'>
                                                            <span className='spinner-border spinner-border-sm' style={{ width: 12, height: 12, borderWidth: 2, color: st.color }} />
                                                            <span style={{ fontSize: 10, fontWeight: 'bold', color: st.color }}>PROCESSANDO</span>
                                                        </span>
                                                    ) : (
                                                        <span style={{
                                                            fontSize: 10,
                                                            fontWeight: 'bold',
                                                            color: st.color,
                                                            backgroundColor: st.bg,
                                                            padding: '2px 8px',
                                                            borderRadius: 4,
                                                            whiteSpace: 'nowrap'
                                                        }}>
                                                            {st.label}
                                                        </span>
                                                    )}
                                                </td>
                                                <td className='text-center'>
                                                    {arquivo.processou && !arquivo.erro ? (
                                                        <button
                                                            className='btn btn-icon btn-sm btn-light-success'
                                                            title='Baixar ZIP'
                                                            onClick={() => handleDownload(arquivo)}
                                                            disabled={baixando === arquivo.id}
                                                        >
                                                            {baixando === arquivo.id ? (
                                                                <span className='spinner-border spinner-border-sm' />
                                                            ) : (
                                                                <i className='fas fa-download fs-6' />
                                                            )}
                                                        </button>
                                                    ) : (
                                                        <span className='text-muted'>—</span>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
        </Modal>
    );
}

export default ModalArquivosRetorno;
